import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "../data/brand";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Our Story", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,padding,box-shadow] duration-500 ${
        scrolled ? "py-3 bg-ivory/80 backdrop-blur-xl shadow-[0_1px_0_rgba(197,161,91,0.35)]" : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="images/SK-logo-nobg.png"
            alt="SK Fragrances"
            className={`transition-all duration-500 object-contain ${scrolled ? "h-9" : "h-12"}`}
          />
          <span className="hidden sm:block font-serif leading-none">
            <span className={`block tracking-[0.35em] text-charcoal transition-all duration-500 ${scrolled ? "text-sm" : "text-base"}`}>
              SK FRAGRANCES
            </span>
            <span className="block text-[10px] tracking-[0.3em] text-gold mt-0.5">SINCE 1980 · BANGALORE</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={`nav-link font-sans text-sm tracking-wide text-charcoal/85 hover:text-charcoal ${
                pathname === n.to ? "active text-charcoal" : ""
              }`}
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="header-enquire-btn"
            className="btn-line border border-charcoal text-charcoal px-6 py-2.5 text-sm tracking-wide hover:text-charcoal"
          >
            Enquire Now →
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-charcoal p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-t border-gold/30"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                  className="font-serif text-3xl text-charcoal"
                >
                  {n.label}
                </Link>
              ))}
              <a
                href={`tel:${BRAND.phone}`}
                className="mt-2 text-sm tracking-widest text-gold"
              >
                {BRAND.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
